import { Mic } from "lucide-react";
import {
  Datagrid,
  DateField,
  DateInput,
  Edit,
  List,
  NumberField,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  useNotify,
  useRedirect,
  useRefresh,
} from "react-admin";
import {
  CardListActions,
  DefaultBulkActionButtons,
  EditorToolbar,
} from "../../components";

const PAYMENT_STATUS_CHOICES = [
  { id: "pending", name: "Pending" },
  { id: "succeeded", name: "Succeeded" },
  { id: "failed", name: "Failed" },
];

const filters = [
  <TextInput source="first_name" label="First Name" alwaysOn />,
  <TextInput source="last_name" label="Last Name" alwaysOn />,
  <TextInput source="email" label="Email" alwaysOn />,
  <ReferenceInput source="lecture_id" reference="lectures" alwaysOn>
    <NumberInput source="lecture_id" label="Lecture ID" />
  </ReferenceInput>,
  <SelectInput
    source="payment_status"
    label="Payment Status"
    choices={PAYMENT_STATUS_CHOICES}
    alwaysOn
  />,
  <DateInput source="date" label="Dedication Date" />,
];

function ListView(props) {
  return (
    <List
      {...props}
      actions={<CardListActions exporter={true} />}
      filters={filters}
      perPage={25}
      sort={{ field: "created_at", order: "DESC" }}
      title="Lecture Dedications"
    >
      <Datagrid
        rowClick="edit"
        bulkActionButtons={<DefaultBulkActionButtons />}
      >
        <TextField source="id" label="ID" />
        <DateField source="created_at" label="Submitted" showTime />
        <TextField source="first_name" label="First Name" />
        <TextField source="last_name" label="Last Name" />
        <TextField source="email" label="Email" />
        <ReferenceField
          source="lecture_id"
          reference="lectures"
          label="Lecture"
          link="edit"
          allowEmpty
        >
          <NumberField source="id" />
        </ReferenceField>
        <DateField source="date" label="Dedication Date" />
        <TextField source="predicate" label="Predicate" />
        <TextField source="dedicatee" label="Dedicatee" />
        <TextField source="payment_method" label="Payment Method" />
        <TextField source="payment_status" label="Payment Status" />
      </Datagrid>
    </List>
  );
}

function EditView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Lecture dedication updated successfully!", {
            type: "success",
          });
          refresh();
          redirect(false);
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      <SimpleForm toolbar={<EditorToolbar showDelete />}>
        <NumberField source="id" label="ID" />
        <ReferenceField
          source="lecture_id"
          reference="lectures"
          label="Lecture"
          link="edit"
        >
          <NumberField source="id" />
        </ReferenceField>
        <TextField source="first_name" label="First Name" />
        <TextField source="last_name" label="Last Name" />
        <TextField source="email" label="Email" />
        <TextField source="payment_method" label="Payment Method" />
        <TextField source="payment_id" label="Payment ID" />
        <DateInput source="date" label="Dedication Date" />
        <TextInput source="preposition1" label="Preposition 1" />
        <TextInput source="predicate" label="Predicate" />
        <TextInput source="preposition2" label="Preposition 2" />
        <TextInput source="dedicatee" label="Dedicatee" />
        <SelectInput
          source="payment_status"
          label="Payment Status"
          choices={PAYMENT_STATUS_CHOICES}
        />
      </SimpleForm>
    </Edit>
  );
}

export const LectureDedications = {
  category: "database",
  name: "lecture_dedications",
  icon: Mic,
  list: ListView,
  edit: EditView,
  options: { label: "Lecture Dedications" },
  roles: ["administrator"],
};
